import { useContext, useEffect, useState, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { fetchStudentCourses } from "../../../services/user/fetchStudentCourses"
import { fetchDepartments } from "../../../services/user/fetchDepartments"
import { DContext } from "../../../context/Datacontext"
import { Pagination } from "../../../utils/Pagination"
import { IoMdHeart } from "react-icons/io";
import { CiHeart } from "react-icons/ci";
import {
  FiSearch,
  FiFilter,
  FiBook,
  FiClock,
  FiTrendingUp,
  FiTag,
  FiGlobe,
  FiAlertCircle,
  FiShoppingCart,
  FiCheckCircle
} from "react-icons/fi"
import { BiRupee } from "react-icons/bi"
import Loading from "../../Loading"
import { addToCart } from "../../../services/students/carts/addToCart"
import { fetchCart } from "../../../services/students/carts/fetchCart"
import { fetchMyCourses } from "../../../services/students/courses/fetchMyCourses"

export const UserDashboard = () => {
  const { currentUser, BeURL } = useContext(DContext)
  const navigate = useNavigate()

  // State Management
  const [courses, setCourses] = useState([])
  const [filteredCourses, setFilteredCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [departments, setDepartments] = useState([])
  const [departmentsLoading, setDepartmentsLoading] = useState(true)
  const [addingCourseId, setAddingCourseId] = useState(null)
  const [addedCourseIds, setAddedCourseIds] = useState([]) // Courses in cart
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]) // Purchased/enrolled courses
  const [actionMessage, setActionMessage] = useState(null)
  const [starCoursesIds,setStarCourses]=useState([])

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("")
  const [departmentFilter, setDepartmentFilter] = useState("")
  const [levelFilter, setLevelFilter] = useState("")
  const [sortBy, setSortBy] = useState("latest")



  // Pagination
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(12)

  // Calculate course price with user discount
  const isUserDiscountActive = useCallback((discount) => {
    if (!discount?.startDate || !discount?.endDate) return false
    const start = new Date(discount.startDate)
    const end = new Date(discount.endDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
    const now = new Date()
    return now >= start && now <= end
  }, [])

  const calculatePrice = useCallback((course) => {
    try {
      if (!course || typeof course !== 'object') return 0

      const basePrice = typeof course.price === 'number' && course.price >= 0 ? course.price : 0

      if (!currentUser || !currentUser._id) {
        if (typeof course.discountPercent === 'number' && course.discountPercent > 0 && course.discountPercent <= 100) {
          const discount = (basePrice * course.discountPercent) / 100
          return Math.max(0, basePrice - discount)
        }
        return basePrice
      }

      const userDiscount = Array.isArray(course.userDiscounts)
        ? course.userDiscounts.find((discount) => discount && typeof discount === 'object' && discount.id === currentUser._id && isUserDiscountActive(discount))
        : null

      if (userDiscount && typeof userDiscount.discountPercent === 'number') {
        const discountPercent = Math.min(100, Math.max(0, userDiscount.discountPercent))
        const discount = (basePrice * discountPercent) / 100
        return Math.max(0, basePrice - discount)
      }

      if (typeof course.discountPercent === 'number' && course.discountPercent > 0 && course.discountPercent <= 100) {
        const discount = (basePrice * course.discountPercent) / 100
        return Math.max(0, basePrice - discount)
      }

      return basePrice
    } catch (err) {
      console.error("Error calculating price:", err)
      return 0
    }
  }, [currentUser, isUserDiscountActive])

  // Fetch courses on mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        if (!BeURL || typeof BeURL !== 'string') {
          setError("Backend URL is not configured properly")
          setLoading(false)
          return
        }

        setLoading(true)
        setError(null)

        const data = await fetchStudentCourses({ BeURL })

        if (!data) {
          throw new Error("No response from server")
        }

        if (data.success) {
          const coursesData = Array.isArray(data.courses) ? data.courses : []
          const validatedCourses = coursesData.filter(course =>
            course && typeof course === 'object' && course._id && course.title
          )
          setCourses(validatedCourses)
          setFilteredCourses(validatedCourses)
        } else {
          throw new Error(data.message || "Failed to load courses")
        }
      } catch (err) {
        console.error("Error loading courses:", err)
        setError(err.message || "Failed to load courses. Please try again later.")
        setCourses([])
        setFilteredCourses([])
      } finally {
        setLoading(false)
      }
    }
    loadCourses()
  }, [BeURL])

  // Fetch departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        if (!BeURL || typeof BeURL !== 'string') {
          setDepartmentsLoading(false)
          return
        }

        setDepartmentsLoading(true)
        const data = await fetchDepartments({ BeURL })

        if (data?.success && Array.isArray(data.departments)) {
          const validDepartments = data.departments.filter(dept =>
            dept && typeof dept === 'object' && dept._id && (dept.name || dept.department)
          )
          setDepartments(validDepartments)
        } else {
          setDepartments([])
        }
      } catch (err) {
        console.error("Error loading departments:", err)
        setDepartments([])
      } finally {
        setDepartmentsLoading(false)
      }
    }
    loadDepartments()
  }, [BeURL])

  // Fetch cart on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        if (!BeURL || typeof BeURL !== 'string') {
          return
        }

        const cartData = await fetchCart({ BeURL })

        if (cartData && cartData.courses && Array.isArray(cartData.courses)) {
          // Convert to strings for consistent comparison
          const cartCourseIds = cartData.courses
            .map(course => {
              const id = course.id || course._id
              return id ? String(id) : null
            })
            .filter(Boolean)
          setAddedCourseIds(cartCourseIds)
        }
      } catch (err) {
        console.error("Error loading cart:", err)
      }
    }
    loadCart()
  }, [BeURL])

  // Fetch orders (purchased courses) on mount
  useEffect(() => {
    const loadOrders = async () => {
      try {
        if (!BeURL || typeof BeURL !== 'string') {
          return
        }
        const result = await fetchMyCourses({ BeURL })
        if (result?.success && Array.isArray(result.orders)){
          const orderCourseIds = result.orders
            .map(order => {
              // Handle both formats: order.course.id (from Order model) and order.course._id
              const courseId = order.course?.id || order.course?._id
              // Convert to string for consistent comparison
              return courseId ? String(courseId) : null
            })
            .filter(Boolean)
          // Store purchased/enrolled courses separately
          setEnrolledCourseIds(orderCourseIds)
        }
      } catch (err) {
        console.error("Error loading orders:", err)
      }
    }
    loadOrders()
  }, [BeURL])


  useEffect(() => {

    const LoaderStarCourses = async () => {
      try {

        const res = await fetch(`${BeURL}/star-courses`, {
          method: "GET",
          credentials: "include"
        })

        const data = await res.json()
        setStarCourses(data.courseIds)
      } catch (err) {
        console.log("Error fetching star courses", err)
      }
    }

    LoaderStarCourses()

  }, [BeURL])

  // Filter and sort logic
  const applyFiltersAndSort = useCallback(() => {
    try {
      let result = [...courses]

      // Search filter
      if (searchQuery && typeof searchQuery === 'string' && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase()
        result = result.filter((course) => {
          try {
            const title = (course?.title || '').toLowerCase()
            const description = (course?.description || '').toLowerCase()
            const deptName = (course?.department?.name || '').toLowerCase()
            return title.includes(query) || description.includes(query) || deptName.includes(query)
          } catch (err) {
            return false
          }
        })
      }

      // Department filter
      if (departmentFilter && typeof departmentFilter === 'string') {
        result = result.filter((course) => {
          try {
            return course?.department?.id === departmentFilter
          } catch (err) {
            return false
          }
        })
      }

      // Level filter
      if (levelFilter && typeof levelFilter === 'string') {
        result = result.filter((course) => {
          try {
            const courseLevel = (course?.level || '').toLowerCase()
            return courseLevel === levelFilter.toLowerCase()
          } catch (err) {
            return false
          }
        })
      }

      // Sorting
      try {
        switch (sortBy) {
          case "latest":
            result.sort((a, b) => {
              const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0
              const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0
              return dateB - dateA
            })
            break
          case "oldest":
            result.sort((a, b) => {
              const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0
              const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0
              return dateA - dateB
            })
            break
          case "price-low":
            result.sort((a, b) => calculatePrice(a) - calculatePrice(b))
            break
          case "price-high":
            result.sort((a, b) => calculatePrice(b) - calculatePrice(a))
            break
          default:
            break
        }
      } catch (err) {
        console.error("Error sorting courses:", err)
      }

      setFilteredCourses(result)
      setPage(1)
    } catch (err) {
      console.error("Error in applyFiltersAndSort:", err)
      setFilteredCourses([])
    }
  }, [courses, searchQuery, departmentFilter, levelFilter, sortBy, calculatePrice])

  // Handle search button click
  const handleSearch = () => {
    applyFiltersAndSort()
  }

  // Handle reset all filters
  const handleReset = () => {
    setSearchQuery("")
    setDepartmentFilter("")
    setLevelFilter("")
    setSortBy("latest")
    setPage(1)
  }

  // Apply filters when filter/sort changes (not search query)
  useEffect(() => {
    applyFiltersAndSort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentFilter, levelFilter, sortBy, courses])

  // Auto-filter only when search is cleared
  useEffect(() => {
    if (searchQuery === '') {
      applyFiltersAndSort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Get original price (for strike-through)
  const getOriginalPrice = (course) => {
    try {
      if (!course || typeof course !== 'object') return 0
      return typeof course.price === 'number' && course.price >= 0 ? course.price : 0
    } catch (err) {
      console.error("Error getting original price:", err)
      return 0
    }
  }

  // Check if price has discount
  const hasDiscount = (course) => {
    try {
      if (!course || typeof course !== 'object') return false

      if (currentUser && currentUser._id && Array.isArray(course.userDiscounts)) {
        const userDiscount = course.userDiscounts.find((discount) => discount && typeof discount === 'object' && discount.id === currentUser._id && isUserDiscountActive(discount))
        if (userDiscount && typeof userDiscount.discountPercent === 'number' && userDiscount.discountPercent > 0) {
          return true
        }
      }

      return typeof course.discountPercent === 'number' && course.discountPercent > 0
    } catch (err) {
      console.error("Error checking discount:", err)
      return false
    }
  }

  // Pagination logic
  const totalPages = useMemo(() => {
    const validLimit = typeof limit === 'number' && limit > 0 ? limit : 12
    const validLength = Array.isArray(filteredCourses) ? filteredCourses.length : 0
    return Math.max(1, Math.ceil(validLength / validLimit))
  }, [filteredCourses, limit])

  const paginatedCourses = useMemo(() => {
    try {
      if (!Array.isArray(filteredCourses)) return []
      const validPage = typeof page === 'number' && page > 0 ? page : 1
      const validLimit = typeof limit === 'number' && limit > 0 ? limit : 12
      const startIndex = (validPage - 1) * validLimit
      const endIndex = startIndex + validLimit
      return filteredCourses.slice(startIndex, endIndex)
    } catch (err) {
      console.error("Error paginating courses:", err)
      return []
    }
  }, [filteredCourses, page, limit])

  // Format price
  const formatPrice = (price) => {
    try {
      const validPrice = typeof price === 'number' && price >= 0 ? price : 0
      return Math.round(validPrice).toLocaleString('en-IN')
    } catch (err) {
      return '0'
    }
  }

  // Generate placeholder image based on course
  const getCoursePlaceholder = (course) => {
    try {
      // Get initials from course title
      const title = course?.title || 'Course'
      const initials = title
        .split(' ')
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2)

      // Color based on level or department
      const level = (course?.level || 'beginner').toLowerCase()
      let bgColor, textColor

      switch (level) {
        case 'beginner':
          bgColor = '#3b82f6' // blue
          textColor = '#ffffff'
          break
        case 'intermediate':
          bgColor = '#8b5cf6' // purple
          textColor = '#ffffff'
          break
        case 'advanced':
          bgColor = '#ef4444' // red
          textColor = '#ffffff'
          break
        default:
          bgColor = '#6b7280' // gray
          textColor = '#ffffff'
      }

      // Create SVG placeholder
      const svg = `
        <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${bgColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${bgColor};stop-opacity:0.7" />
            </linearGradient>
          </defs>
          <rect width="400" height="300" fill="url(#grad)"/>
          <g transform="translate(200, 120)">
            <circle r="50" fill="rgba(255,255,255,0.2)" />
            <text x="0" y="0" text-anchor="middle" dominant-baseline="middle" 
                  font-family="system-ui, -apple-system, sans-serif" 
                  font-size="48" font-weight="bold" fill="${textColor}">
              ${initials}
            </text>
          </g>
          <text x="200" y="230" text-anchor="middle" 
                font-family="system-ui, -apple-system, sans-serif" 
                font-size="18" font-weight="600" fill="${textColor}" opacity="0.9">
            ${level.charAt(0).toUpperCase() + level.slice(1)} Level
          </text>
        </svg>
      `.trim()

      // Convert to data URI
      return `data:image/svg+xml;base64,${btoa(svg)}`
    } catch (err) {
      console.error('Error generating placeholder:', err)
      // Fallback simple SVG
      const fallbackSvg = `
        <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
          <rect width="400" height="300" fill="#6b7280"/>
          <text x="200" y="150" text-anchor="middle" dominant-baseline="middle" 
                font-family="system-ui, sans-serif" font-size="48" fill="white">
            📚
          </text>
        </svg>
      `.trim()
      return `data:image/svg+xml;base64,${btoa(fallbackSvg)}`
    }
  }

  const handleAddToCart = async (courseId) => {
    if (!courseId || !BeURL) return

    setAddingCourseId(courseId)
    const result = await addToCart({ BeURL, courseId })

    if (result?.success) {
      // Convert to string for consistent comparison
      const courseIdStr = String(courseId)
      setAddedCourseIds((prev) => (prev.includes(courseIdStr) ? prev : [...prev, courseIdStr]))
      setActionMessage({ type: 'success', text: 'Course added to cart successfully!' })
    } else if (result?.message) {
      setActionMessage({ type: 'error', text: result.message })
    }

    setAddingCourseId(null)
    setTimeout(() => setActionMessage(null), 2500)
  }


  const handleActionHeart = async (id, type) => {
    try {
      const res = await fetch(`${BeURL}/course-like?id=${id}&type=${type}`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await res.json();
      console.log(data);
    } catch (error) {
      console.log("Error in course like:", error);
    }
  };


  if (loading || departmentsLoading) return <Loading />

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <FiAlertCircle size={32} className="text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Courses</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-medium rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <FiBook className="text-primary-600" />
            Explore Courses
          </h1>
          <p className="text-gray-600">
            Discover {filteredCourses.length || 0} courses to boost your skills
          </p>
        </div>

        {actionMessage && (
          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm animate-in fade-in slide-in-from-top-2 ${actionMessage.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}>
            {actionMessage.text}
          </div>
        )}

        {/* Search & Filters Section */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 animate-in fade-in slide-in-from-top-4 duration-700">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="search"
                  placeholder="Search courses by title, description, or department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  maxLength={200}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all outline-none"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-medium rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
              >
                <FiSearch size={18} />
                <span className="hidden sm:inline">Search</span>
              </button>
              <button
                onClick={handleReset}
                className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                title="Reset all filters"
              >
                <FiFilter size={18} />
                <span className="hidden sm:inline">Reset</span>
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Department Filter */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                <FiFilter size={16} />
                Department
              </label>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all outline-none bg-white"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => {
                  const name = dept.department || dept.name || "";

                  const formatted = name.replace(/\((.*?)\)/, (match, p1) => {
                    return `(${p1.toUpperCase()})`;
                  });

                  return (
                    <option key={dept._id} value={dept._id}>
                      {formatted}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Level Filter */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                <FiTrendingUp size={16} />
                Level
              </label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all outline-none bg-white"
              >
                <option value="">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                <FiClock size={16} />
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all outline-none bg-white"
              >
                <option value="latest">Latest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Courses Grid */}
        {paginatedCourses.length === 0 ? (
          <div className="text-center py-16 animate-in fade-in duration-500">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <FiBook size={48} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No courses found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {paginatedCourses.map((course, index) => {
                  if (!course || !course._id) return null

                  const deptName = (course.department?.name || "General").replace(
                    /\((.*?)\)/,
                    (match, p1) => `(${p1.toUpperCase()})`
                  )

                  const isEnrolled = enrolledCourseIds.includes(String(course._id))
                  const isAdded = addedCourseIds.includes(String(course._id))
                  const isStar = starCoursesIds.includes(String(course?._id))

                  return (
                    <div
                      key={course._id}
                      onClick={() => navigate(`/learner/courses/${course._id}`)}
                      className="group bg-secondary-50 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:-translate-y-1 cursor-pointer"
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      {/* Thumbnail */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={course.thumbnail?.url || getCoursePlaceholder(course)}
                          alt={course.title || "Course"}
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = getCoursePlaceholder(course)
                          }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />

                        {/* Level Badge */}
                        <span className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1 text-xs font-semibold rounded-full text-primary-600 capitalize shadow-sm">
                          {course.level || "Beginner"}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="p-5 flex flex-col  justify-between space-y-4">
                        <div>
                          {/* Title */}
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-600 transition">
                            {(course.title || "Untitled Course").replace(/\((.*?)\)/, (m, p) => `(${p.toUpperCase()})`)}
                          </h3>

                          {/* Description */}
                          <div className="text-sm text-gray-500 mt-1">
                            <p className="line-clamp-2">
                              {course?.shortDescription || "No description available"}
                            </p>

                            {course?.shortDescription && course.shortDescription.length > 80 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/learner/courses/${course._id}`)
                                }}
                                className="text-primary-600 hover:underline text-xs font-medium mt-1"
                              >
                                See more
                              </button>
                            )}
                          </div>
                          {/* Meta */}
                          <div className="flex flex-wrap gap-2 mt-3 text-xs">
                            <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-gray-600">
                              <FiTag size={12} />
                              {deptName}
                            </span>

                            <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md text-gray-600">
                              <FiGlobe size={12} />
                              {course.language || "English"}
                            </span>
                          </div>
                        </div>

                        {/* Price + Button */}
                        <div className="flex items-center justify-between pt-4 border-t mt-4">

                          {/* Price */}
                          <div>
                            {hasDiscount(course) ? (
                              <div className="flex items-center gap-2">
                                <span className="flex items-center text-gray-400 line-through text-sm">
                                  <BiRupee size={14} />
                                  {formatPrice(getOriginalPrice(course))}
                                </span>

                                <span className="flex items-center text-primary-600 font-bold text-lg">
                                  <BiRupee size={18} />
                                  {formatPrice(calculatePrice(course))}
                                </span>
                              </div>
                            ) : (
                              <span className="flex items-center text-primary-600 font-bold text-lg">
                                <BiRupee size={18} />
                                {formatPrice(getOriginalPrice(course))}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">

                          <button>
                            {isStar ? (
                              <div onClick={() => handleActionHeart(course?._id, "unstar")}  className="flex items-center gap-1 text-sm text-secondary-500">
                                <IoMdHeart size={30}/>
                              </div>
                            ) : (
                              <div onClick={()=>handleActionHeart(course?._id , "star")} className="flex items-center gap-1 text-sm">
                                <CiHeart size={30} />
                              </div>
                            )}
                          </button>
                          {/* Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()

                              if (isEnrolled) {
                                navigate(`/user/course/${course._id}`)
                              } else if (isAdded) {
                                navigate("/learner/cart")
                              } else {
                                handleAddToCart(course._id)
                              }
                            }}
                            disabled={addingCourseId === course._id}
                            className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200
              
                              ${isEnrolled
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : isAdded
                                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                  : "bg-gradient-to-r from-primary-600 to-secondary-500 text-white hover:shadow-md"
                              }
              
                           ${addingCourseId === course._id ? "opacity-70 cursor-wait" : ""}
                            `}
                          >
                            {isEnrolled ? (
                              <>
                                <FiCheckCircle size={16} />
                                Enrolled
                              </>
                            ) : isAdded ? (
                              <>
                                <FiCheckCircle size={16} />
                                Added
                              </>
                            ) : (
                              <>
                                <FiShoppingCart size={16} />
                                {addingCourseId === course._id ? "Adding..." : "Add to cart"}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

            {/* Pagination */}
            {totalPages > 0 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                limit={limit}
                totalUsers={filteredCourses.length || 0}
                setPage={setPage}
                setLimit={setLimit}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
